import { StateCreator } from "zustand";

import { RootState } from "store";
import { ErrorItem, ErrorItemType } from "models/errors/errors";
import { isTokenExpired } from "helpers/auth/token";
import { getTabToken } from "common/MetaActions";

export interface ErrorSlice {
  errorList: ErrorItem[];

  appendErrorNotification: (errorText: string, errorObj: any) => void;
  removeError: (error: ErrorItem) => void;
  handleCustomWarningMessage: (statusCode: number, messageText: string) => void;
}
const getId = () => `${Math.random()}`.split(".")[1];
export const createErrorSlice: StateCreator<
  RootState,
  [["zustand/devtools", never]],
  [],
  ErrorSlice
> = (set, get) => ({
  errorList: [],

  appendErrorNotification: (
    errorText = "Ett oväntat fel har inträffat. Försök igen.",
    errorObj
  ) => {
    // ---Detect Token expired ---
    const token = getTabToken();

    if (token && isTokenExpired(token)) {
      console.warn("Session expired due to expired token.");

      get().user.setSessionExpired(true);

      return;
    }

    // --- Detect abort requests ---
    const errorMessage =
      typeof errorObj === "string" ? errorObj : errorObj?.message || "";
    const errorName = errorObj?.name?.toLowerCase?.() || "";
    const errorCode = errorObj?.code?.toLowerCase?.() || "";
    const errorStatusCode = errorObj?.statusCode;
    const isAbortError =
      errorName === "aborterror" || // fetch abort
      errorCode === "err_canceled" || // axios cancel
      (errorStatusCode === 0 && !errorMessage) || // network aborted
      errorObj?.code === "ABORTED"; // superagent abort

    if (isAbortError) {
      console.warn(
        "Request was aborted or terminated — skipping error notification.",
        errorObj
      );
      return;
    }

    // --- Build error details ---
    let additionalErrorText = "";
    let statusCode;

    if (typeof errorObj === "string") {
      // If errorObj is just a string, use it directly
      additionalErrorText = errorObj;
    } else if (errorObj && typeof errorObj.message === "string") {
      try {
        const parsed = JSON.parse(errorObj.message);
        additionalErrorText = parsed.message || errorObj.message;
      } catch (e) {
        additionalErrorText = errorObj.message;
      }
      statusCode = errorObj.statusCode;
    } else if (errorObj && errorObj.message !== undefined) {
      additionalErrorText = String(errorObj.message);
      statusCode = errorObj.statusCode;
    } else if (errorObj) {
      // errorObj exists but has no .message show the errorObj as string
      additionalErrorText = JSON.stringify(errorObj);
      statusCode = errorObj.statusCode;
    }

    set(
      ({ error }) => ({
        error: {
          ...error,
          errorList: [
            ...error.errorList,
            {
              statusCode,
              additionalErrorText,
              errorText,
              id: getId(),
              type: ErrorItemType.error
            }
          ]
        }
      }),
      false,
      "error/APPEND_ERROR_NOTIFICATION"
    );
  },

  removeError: (payloadError) => {
    const filteredErrorList = get().error.errorList.filter(
      (err) => err.id !== payloadError.id
    );
    set(
      ({ error }) => ({
        error: {
          ...error,
          errorList: filteredErrorList
        }
      }),
      false,
      "error/REMOVE_ERROR"
    );
  },

  handleCustomWarningMessage: (statusCode, messageText) => {
    const newError = {
      statusCode: statusCode,
      additionalErrorText: "",
      errorText: messageText,
      id: getId(),
      type: ErrorItemType.warning
    };

    set(
      ({ error }) => ({
        error: {
          ...error,
          errorList: [...error.errorList, newError]
        }
      }),
      false,
      "error/HANDLE_CUSTOM_WARNING_MESSAGE"
    );
  }
});
